import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import affiliateMarketingService from "../requests/admin/AffiliateMarketingService";

const usePotentialMerchants = () => {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 15,
    hasNext: false,
    hasPrev: false,
  });
  const [filters, setFilters] = useState({
    search: "",
    sortBy: "created_at",
    sortOrder: "desc",
    dateFrom: "",
    dateTo: "",
    network: "",
  });

  const cache = useRef(new Map());
  const searchTimeoutRef = useRef(null);

  const debouncedSearch = useCallback((searchTerm) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchTerm }));
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    }, 500);
  }, []);

  const getCacheKey = useCallback(
    (page, limit, search, sortBy, sortOrder, dateFrom, dateTo, network) =>
      `${page}-${limit}-${search}-${sortBy}-${sortOrder}-${dateFrom}-${dateTo}-${network}`,
    []
  );

  const computePaginationData = useCallback((page, limit, totalItems) => {
    const totalPages = Math.max(1, Math.ceil((totalItems || 0) / limit));
    const currentPage = Math.max(Math.min(Math.max(page || 1, 1), totalPages), 1);
    return {
      currentPage,
      totalPages,
      totalItems: totalItems || 0,
      itemsPerPage: limit,
      hasNext: currentPage < totalPages,
      hasPrev: currentPage > 1,
    };
  }, []);

  const fetchMerchants = useCallback(
    async (
      page = 1,
      limit = 15,
      search = "",
      sortBy = "created_at",
      sortOrder = "desc",
      dateFrom = "",
      dateTo = "",
      network = ""
    ) => {
      const safePage = Math.max(page || 1, 1);
      const cacheKey = getCacheKey(
        safePage,
        limit,
        search,
        sortBy,
        sortOrder,
        dateFrom,
        dateTo,
        network
      );

      if (cache.current.has(cacheKey)) {
        const cached = cache.current.get(cacheKey);
        setMerchants(cached.data);
        setPagination(cached.pagination);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await affiliateMarketingService.getMerchantsPaginated(
          safePage,
          limit,
          search,
          sortBy,
          sortOrder,
          dateFrom,
          dateTo,
          network
        );

        if (!response.status) {
          setError(response.message || "Error fetching merchants");
          toast.error(response.message || "Error fetching merchants");
          return;
        }

        const list = Array.isArray(response.data) ? response.data : [];

        const p = response.pagination || {};
        const totalItems =
          p.total_items ??
          p.total ??
          p.totalItems ??
          (typeof p.total === "string" ? parseInt(p.total, 10) : undefined) ??
          list.length;
        const totalPages =
          p.total_pages ??
          p.totalPages ??
          (typeof p.last_page === "number" ? p.last_page : undefined) ??
          Math.max(1, Math.ceil((totalItems || 0) / limit));
        const currentPage = p.current_page ?? p.currentPage ?? safePage;

        const computed = computePaginationData(currentPage, limit, totalItems);
        // If server supplies has_next/has_prev, prefer them.
        const hasNext = p.has_next ?? p.hasNext ?? computed.hasNext;
        const hasPrev = p.has_prev ?? p.hasPrev ?? computed.hasPrev;

        setMerchants(list);
        setPagination({
          ...computed,
          hasNext,
          hasPrev,
          totalPages,
        });

        if (cache.current.size > 50) {
          const firstKey = cache.current.keys().next().value;
          cache.current.delete(firstKey);
        }
        cache.current.set(cacheKey, {
          data: list,
          pagination: { ...computed, hasNext, hasPrev, totalPages },
          timestamp: Date.now(),
        });
      } catch (err) {
        setError("Network error occurred");
        toast.error("Network error occurred");
        console.error("Error fetching merchants:", err);
      } finally {
        setLoading(false);
      }
    },
    [computePaginationData, getCacheKey]
  );

  useEffect(() => {
    fetchMerchants(
      Math.max(pagination.currentPage || 1, 1),
      pagination.itemsPerPage || 15,
      filters.search,
      filters.sortBy,
      filters.sortOrder,
      filters.dateFrom,
      filters.dateTo,
      filters.network
    );
  }, [
    pagination.currentPage,
    pagination.itemsPerPage,
    filters.search,
    filters.sortBy,
    filters.sortOrder,
    filters.dateFrom,
    filters.dateTo,
    filters.network,
    fetchMerchants,
  ]);

  useEffect(() => {
    cache.current.clear();
  }, [
    filters.search,
    filters.sortBy,
    filters.sortOrder,
    filters.dateFrom,
    filters.dateTo,
    filters.network,
  ]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const changePage = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const changeItemsPerPage = useCallback((newLimit) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
      itemsPerPage: newLimit,
    }));
  }, []);

  const updateSort = useCallback((sortBy, sortOrder) => {
    setFilters((prev) => ({ ...prev, sortBy, sortOrder }));
  }, []);

  const updateDateFilter = useCallback((dateFrom, dateTo) => {
    setFilters((prev) => ({ ...prev, dateFrom, dateTo }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const updateNetworkFilter = useCallback((network) => {
    setFilters((prev) => ({ ...prev, network }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const clearDateFilter = useCallback(() => {
    setFilters((prev) => ({ ...prev, dateFrom: "", dateTo: "" }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({
      search: "",
      sortBy: "created_at",
      sortOrder: "desc",
      dateFrom: "",
      dateTo: "",
      network: "",
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const refetch = useCallback(() => {
    cache.current.clear();
    fetchMerchants(
      pagination.currentPage,
      pagination.itemsPerPage,
      filters.search,
      filters.sortBy,
      filters.sortOrder,
      filters.dateFrom,
      filters.dateTo,
      filters.network
    );
  }, [fetchMerchants, filters, pagination.currentPage, pagination.itemsPerPage]);

  /**
   * Fetch ALL merchants matching the current filters from the backend
   * (not just the current page) and return the full list for client-side export.
   */
  const fetchAllMatchingForExport = useCallback(async () => {
    const EXPORT_LIMIT = 500;
    const MAX_PAGES = 200;

    const all = [];
    const seen = new Set();

    let page = 1;
    let totalPages = 1;

    while (page <= totalPages && page <= MAX_PAGES) {
      const response = await affiliateMarketingService.getMerchantsPaginated(
        page,
        EXPORT_LIMIT,
        filters.search,
        filters.sortBy,
        filters.sortOrder,
        filters.dateFrom,
        filters.dateTo,
        filters.network
      );

      if (!response.status) {
        throw new Error(response.message || "Failed to fetch export data");
      }

      const list = Array.isArray(response.data) ? response.data : [];
      for (const item of list) {
        const key = item?.id ?? JSON.stringify(item);
        if (!seen.has(key)) {
          seen.add(key);
          all.push(item);
        }
      }

      const p = response.pagination || {};
      const maybeTotalPages =
        p.total_pages ??
        p.totalPages ??
        p.last_page ??
        (p.total && Math.ceil(Number(p.total) / EXPORT_LIMIT)) ??
        (p.total_items && Math.ceil(Number(p.total_items) / EXPORT_LIMIT));

      if (typeof maybeTotalPages === "number" && Number.isFinite(maybeTotalPages)) {
        totalPages = Math.max(1, maybeTotalPages);
      } else {
        // If backend doesn't provide pagination, stop when we get less than limit.
        totalPages = list.length < EXPORT_LIMIT ? page : page + 1;
      }

      page += 1;
    }

    return all;
  }, [
    filters.dateFrom,
    filters.dateTo,
    filters.network,
    filters.search,
    filters.sortBy,
    filters.sortOrder,
  ]);

  return {
    merchants,
    loading,
    error,
    pagination,
    filters,
    debouncedSearch,
    changePage,
    changeItemsPerPage,
    updateSort,
    updateDateFilter,
    updateNetworkFilter,
    clearDateFilter,
    clearAllFilters,
    refetch,
    fetchAllMatchingForExport,
  };
};

export default usePotentialMerchants;
